import {
  Arrows,
  isSegmentStartCloserToElementStart,
  useRenderPipeDirection,
} from '@trenches/components/PipeDirection';
import { trenchSelectors } from '@trenches/redux';
import { enableLayerOne, getCachedMaterial, getRenderOrder } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { FC, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TubeGeometry } from 'three';
import type { TrenchSegmentProps } from './types';
import { useTrenchSegmentData } from './useTrenchSegmentData';

export const TrenchSegment: FC<TrenchSegmentProps> = ({
  isMouseOverTrench,
  isTrenchSelected,
  segment,
  curve,
  mode,
  selectedAttributeType,
}) => {
  const { selectedElement, opacity, color, radius, isSelectedElementInSegment } =
    useTrenchSegmentData({
      isMouseOverTrench,
      isTrenchSelected,
      segment,
      mode,
      selectedAttributeType,
    });
  const renderPipeDirection = useRenderPipeDirection();

  const segmentCurve = useMemo(
    () =>
      curve.cloneWithPointIndexBoundaries(
        segment.sequenceRange.fromIndex,
        segment.sequenceRange.toIndex,
      ),
    [curve, segment.sequenceRange],
  );

  const allTrenches = useSelector(trenchSelectors.selectAll);

  const geometry = useMemo(() => {
    if (!segmentCurve) return null;
    return new TubeGeometry(segmentCurve, segmentCurve.getSegments(), radius, 8, false);
  }, [segmentCurve, radius]);

  useEffect(() => {
    return () => {
      geometry?.dispose();
    };
  }, [geometry]);

  if (!segmentCurve || !geometry) return null;

  const isPipeDirectionAscending =
    isSelectedElementInSegment &&
    !!selectedElement &&
    !isSegmentStartCloserToElementStart(segment, selectedElement, allTrenches);

  const material = getCachedMaterial(color, opacity, { depthTest: false });
  const shouldRenderArrows = isSelectedElementInSegment && renderPipeDirection;
  return (
    <>
      {shouldRenderArrows && (
        <Arrows curve={segmentCurve} isPipeDirectionAscending={isPipeDirectionAscending} />
      )}
      <mesh
        name={`${Object3dNames.TrenchSegment}:${segment.fromPointId}-${segment.toPointId}`}
        material={material}
        geometry={geometry}
        renderOrder={getRenderOrder(Object3dNames.TrenchSegment)}
        onUpdate={enableLayerOne}
        frustumCulled={false}
      />
    </>
  );
};
